import React, { useState, useEffect } from 'react';
import axios from 'axios';

const Billing = () => {
  const [settings, setSettings] = useState(null);
  const [history, setHistory] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [autoCharge, setAutoCharge] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, historyRes, summaryRes] = await Promise.all([
        axios.get('/api/billing/settings'),
        axios.get('/api/billing/history'),
        axios.get('/api/billing/summary')
      ]);

      setSettings(settingsRes.data.settings);
      setHistory(historyRes.data.records);
      setSummary(summaryRes.data.summary);
      // Price is read-only, don't set it in state
      setAutoCharge(settingsRes.data.settings?.auto_charge !== false);
    } catch (error) {
      console.error('Failed to fetch billing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaveLoading(true);
    try {
      // Only send autoCharge - pricing cannot be changed by clients
      await axios.put('/api/billing/settings', {
        autoCharge
      });
      setEditing(false);
      fetchData();
    } catch (error) {
      console.error('Failed to update settings:', error);
      const errorMsg = error.response?.data?.error || 'Failed to update settings';
      alert(errorMsg);
    } finally {
      setSaveLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading billing data...</div>;
  }

  return (
    <div className="container">
      <div className="dashboard-header">
        <h1 style={{ 
          background: 'var(--gradient-primary)', 
          WebkitBackgroundClip: 'text', 
          WebkitTextFillColor: 'transparent' 
        }}>
          Billing & Settings
        </h1>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '24px' }}>
        {/* Settings Card */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <h2 style={{ margin: 0 }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--accent-primary)" strokeWidth="2" style={{ marginRight: '8px' }}>
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
              Billing Settings
            </h2>
            {!editing && (
              <button onClick={() => setEditing(true)} className="btn btn-secondary" style={{ padding: '8px 16px', fontSize: '14px' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
                Edit
              </button>
            )}
          </div>

          {editing ? (
            <div>
              <div className="form-group">
                <label>Price Per Click (USD)</label>
                <div style={{
                  padding: '12px 16px',
                  background: 'var(--bg-elevated)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-color)',
                  color: 'var(--text-muted)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <span>${parseFloat(settings?.price_per_click || 0).toFixed(3)}</span>
                  <span style={{ fontSize: '12px', fontStyle: 'italic' }}>
                    Set by service provider
                  </span>
                </div>
                <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '8px' }}>
                  Pricing is set by the service provider and cannot be changed.
                </p>
              </div>
              <div className="form-group">
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '12px',
                  cursor: 'pointer',
                  padding: '16px',
                  background: 'var(--bg-elevated)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-color)'
                }}>
                  <input
                    type="checkbox"
                    checked={autoCharge}
                    onChange={(e) => setAutoCharge(e.target.checked)}
                    style={{ 
                      width: '20px', 
                      height: '20px',
                      accentColor: 'var(--accent-primary)'
                    }}
                  />
                  <div>
                    <span style={{ display: 'block', fontWeight: 500, color: 'var(--text-primary)' }}>
                      Auto-charge on each click
                    </span>
                    <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                      Automatically record charges when QR codes are scanned
                    </span>
                  </div>
                </label>
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button onClick={handleSaveSettings} className="btn btn-primary" disabled={saveLoading}>
                  {saveLoading ? 'Saving...' : 'Save Changes'}
                </button>
                <button onClick={() => {
                  setEditing(false);
                  setAutoCharge(settings?.auto_charge !== false);
                }} className="btn btn-secondary">
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '16px',
                background: 'var(--bg-elevated)',
                borderRadius: 'var(--radius-md)'
              }}>
                <span style={{ color: 'var(--text-muted)' }}>Price Per Click</span>
                <span style={{ 
                  fontWeight: 600, 
                  fontSize: '18px',
                  background: 'var(--gradient-primary)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  ${parseFloat(settings?.price_per_click || 0).toFixed(3)}
                </span>
              </div>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '16px',
                background: 'var(--bg-elevated)',
                borderRadius: 'var(--radius-md)'
              }}>
                <span style={{ color: 'var(--text-muted)' }}>Currency</span>
                <span style={{ fontWeight: 500 }}>{settings?.currency || 'USD'}</span>
              </div>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '16px',
                background: 'var(--bg-elevated)',
                borderRadius: 'var(--radius-md)'
              }}>
                <span style={{ color: 'var(--text-muted)' }}>Auto-charge</span>
                <span style={{
                  padding: '4px 12px',
                  borderRadius: 'var(--radius-full)',
                  background: settings?.auto_charge ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                  color: settings?.auto_charge ? 'var(--accent-primary)' : 'var(--accent-danger)',
                  fontSize: '13px',
                  fontWeight: 500
                }}>
                  {settings?.auto_charge ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '16px',
                background: 'var(--bg-elevated)',
                borderRadius: 'var(--radius-md)'
              }}>
                <span style={{ color: 'var(--text-muted)' }}>Balance</span>
                <span style={{ fontWeight: 600, fontSize: '18px' }}>
                  ${parseFloat(settings?.balance || 0).toFixed(2)}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Summary Card */}
        {summary && (
          <div className="card">
            <h2>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--accent-secondary)" strokeWidth="2" style={{ marginRight: '8px' }}>
                <line x1="12" y1="1" x2="12" y2="23"/>
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
              </svg>
              Billing Summary
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="stat-card">
                <h3>Total Charged</h3>
                <div className="value">${parseFloat(summary.total_charged || 0).toFixed(2)}</div>
              </div>
              <div className="stat-card">
                <h3>Clicks Charged</h3>
                <div className="value">{summary.total_clicks_charged || 0}</div>
              </div>
              <div className="stat-card">
                <h3>Pending</h3>
                <div className="value" style={{ color: 'var(--accent-warning)' }}>{summary.pending_charges || 0}</div>
              </div>
              <div className="stat-card">
                <h3>Completed</h3>
                <div className="value">{summary.completed_charges || 0}</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* History Card */}
      <div className="card" style={{ marginTop: '24px' }}>
        <h2>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--accent-tertiary)" strokeWidth="2" style={{ marginRight: '8px' }}>
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12 6 12 12 16 14"/>
          </svg>
          Billing History
        </h2>
        {history.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>QR Code</th>
                </tr>
              </thead>
              <tbody>
                {history.map((record) => (
                  <tr key={record.id}>
                    <td>
                      <span style={{ color: 'var(--text-primary)' }}>
                        {new Date(record.created_at).toLocaleDateString()}
                      </span>
                      <span style={{ color: 'var(--text-muted)', marginLeft: '8px', fontSize: '13px' }}>
                        {new Date(record.created_at).toLocaleTimeString()}
                      </span>
                    </td>
                    <td>
                      <span style={{ fontWeight: 600, color: 'var(--accent-primary)' }}>
                        ${parseFloat(record.amount).toFixed(4)}
                      </span>
                      <span style={{ color: 'var(--text-muted)', marginLeft: '4px', fontSize: '13px' }}>
                        {record.currency}
                      </span>
                    </td>
                    <td>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: 'var(--radius-full)',
                        background: record.status === 'completed' 
                          ? 'rgba(16, 185, 129, 0.15)' 
                          : 'rgba(245, 158, 11, 0.15)',
                        color: record.status === 'completed' 
                          ? 'var(--accent-primary)' 
                          : 'var(--accent-warning)',
                        fontSize: '12px',
                        fontWeight: 500,
                        textTransform: 'capitalize'
                      }}>
                        {record.status}
                      </span>
                    </td>
                    <td>
                      <code style={{ fontSize: '13px' }}>
                        {record.qr_title || record.short_code || 'N/A'}
                      </code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px 20px',
            color: 'var(--text-muted)'
          }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ opacity: 0.5, marginBottom: '16px' }}>
              <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
              <line x1="1" y1="10" x2="23" y2="10"/>
            </svg>
            <p>No billing records yet</p>
            <p style={{ fontSize: '14px' }}>Charges will appear here when QR codes are scanned</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Billing;
